import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { rateRelease, type RatingResult } from './releases'
import { usePortalSession } from './usePortalSession'

interface RatingStarsProps {
  releaseNoteId: string
  avgRating: number | null
  ratingsCount: number
  /** Voto propio ya conocido (llega tras hidratar sesión); null = sin voto. */
  myScore?: number | null
}

const SCORES = [1, 2, 3, 4, 5]

/**
 * Puntuación 1–5 estrellas de una nota de versión (decisión 6 del goal):
 * solo usuarios logueados votan (PUT con Bearer, re-votar actualiza); el
 * anónimo ve el promedio en solo lectura + aviso de iniciar sesión. Sin
 * efectos: el agregado mostrado es el del último voto propio (respuesta del
 * PUT) o el que llegó por props.
 */
export function RatingStars({
  releaseNoteId,
  avgRating,
  ratingsCount,
  myScore = null,
}: RatingStarsProps) {
  const { t } = useTranslation()
  const { isLoggedIn } = usePortalSession()
  const [result, setResult] = useState<RatingResult | null>(null)
  const [hover, setHover] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const shownAvg = result ? result.avgRating : avgRating
  const shownCount = result ? result.ratingsCount : ratingsCount
  const shownScore = result ? result.myScore : myScore

  const highlighted = hover ?? shownScore ?? Math.round(shownAvg ?? 0)

  const rate = async (score: number) => {
    if (saving) return
    setSaving(true)
    setFailed(false)
    try {
      setResult(await rateRelease(releaseNoteId, score))
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const star = (score: number) => (
    <Star
      className={`size-5 transition-colors ${
        score <= highlighted
          ? 'fill-amber-400 text-amber-400'
          : 'fill-transparent text-neutral-300'
      }`}
    />
  )

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {isLoggedIn ? (
        <div
          role="group"
          aria-label={t('releases.rating.groupAria')}
          className="flex items-center"
          onMouseLeave={() => setHover(null)}
        >
          {SCORES.map((score) => (
            <button
              key={score}
              type="button"
              disabled={saving}
              aria-label={t('releases.rating.rateAria', { count: score })}
              aria-pressed={shownScore === score}
              onMouseEnter={() => setHover(score)}
              onFocus={() => setHover(score)}
              onBlur={() => setHover(null)}
              onClick={() => void rate(score)}
              className="p-0.5 disabled:opacity-60"
            >
              {star(score)}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex items-center" aria-hidden="true">
          {SCORES.map((score) => (
            <span key={score} className="p-0.5">
              {star(score)}
            </span>
          ))}
        </div>
      )}

      <span className="text-sm text-muted-foreground">
        {shownCount > 0
          ? `${(shownAvg ?? 0).toFixed(1)} · ${t('releases.rating.votes', { count: shownCount })}`
          : t('releases.rating.noVotes')}
      </span>

      {isLoggedIn && shownScore != null && (
        <span className="text-sm font-medium text-foreground">
          {t('releases.rating.yourScore', { score: shownScore })}
        </span>
      )}

      {!isLoggedIn && (
        <Link
          to="/login"
          className="text-sm font-semibold text-primary hover:underline"
        >
          {t('releases.rating.signIn')}
        </Link>
      )}

      {failed && (
        <span role="alert" className="text-sm text-red-600">
          {t('releases.rating.error')}
        </span>
      )}
    </div>
  )
}
